import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  clearStorePaymentLinkReturn,
  createStoreCheckoutSession,
  fetchStoreProduct,
  isStripeHostedCheckoutUrl,
  MAX_STORE_CHECKOUT_QUANTITY,
  rememberStorePaymentLinkReturn,
  STORE_CHECKOUT_MODE_PAYMENT_LINK,
} from '../lib/store';

function ProductFact({ label, value }) {
  if (!value) {
    return null;
  }

  return (
    <div className="rounded-[22px] border border-slate-100 bg-slate-50/70 px-5 py-5">
      <p className="text-[10px] uppercase tracking-[0.28em] text-slate-500">{label}</p>
      <p className="mt-4 text-lg leading-7 text-slate-900">{value}</p>
    </div>
  );
}

export default function StoreProductPage() {
  const { slug = '' } = useParams();
  const [searchParams] = useSearchParams();
  const [quantity, setQuantity] = useState(1);
  const [customerEmail, setCustomerEmail] = useState('');
  const { data: product, isLoading, error } = useQuery({
    queryKey: ['storeProduct', slug],
    queryFn: () => fetchStoreProduct(slug),
    enabled: Boolean(slug),
  });
  const checkoutMutation = useMutation({
    mutationFn: async () => {
      const usesStripePaymentLink = isStripeHostedCheckoutUrl(product?.checkoutUrl);
      const session = await createStoreCheckoutSession({
        slug: product?.slug || slug,
        quantity: usesStripePaymentLink ? 1 : quantity,
        email: usesStripePaymentLink ? '' : customerEmail.trim(),
      });

      if (!session.url) {
        throw new Error('Checkout session did not return a redirect URL');
      }

      if (session.checkoutMode === STORE_CHECKOUT_MODE_PAYMENT_LINK) {
        rememberStorePaymentLinkReturn({
          checkoutUrl: session.url,
          productName: product?.name,
          productSlug: product?.slug || slug,
        });
      } else {
        clearStorePaymentLinkReturn();
      }

      window.location.assign(session.url);
      return session;
    },
  });

  if (isLoading) {
    return (
      <main className="page-shell">
        <div className="page-narrow surface-card flex min-h-[280px] items-center justify-center text-slate-600">
          Loading product...
        </div>
      </main>
    );
  }

  if (error || !product) {
    return (
      <main className="page-shell">
        <div className="page-narrow">
          <Link to="/store" className="text-link">
            Back to store
          </Link>
          <div className="mt-10 rounded-[28px] border border-rose-200 bg-rose-50 p-6 text-rose-700">
            {error?.message || 'This product could not be loaded.'}
          </div>
        </div>
      </main>
    );
  }

  const description = product.description || product.shortDescription || 'Product details will be added soon.';
  const checkoutWasCanceled = searchParams.get('checkout') === 'canceled';
  const usesStripePaymentLink = isStripeHostedCheckoutUrl(product.checkoutUrl);
  const canStartCheckout = product.inStock;
  const canStartHostedCheckout = canStartCheckout && !usesStripePaymentLink;
  const canStartPaymentLinkCheckout = canStartCheckout && usesStripePaymentLink;
  const maxCheckoutQuantity = Math.max(
    1,
    Math.min(
      MAX_STORE_CHECKOUT_QUANTITY,
      Number(product.stockCount) > 0 ? Number(product.stockCount) : MAX_STORE_CHECKOUT_QUANTITY,
    ),
  );

  return (
    <main className="page-shell">
      <article className="page-width">
        <Link to="/store" className="text-link">
          Back to store
        </Link>

        {checkoutWasCanceled ? (
          <div className="mt-8 rounded-[24px] border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-800">
            Checkout was canceled before payment completed. You can start again whenever you are ready.
          </div>
        ) : null}

        <div className="mt-10 grid gap-8 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)]">
          <section className="surface-card overflow-hidden">
            <div className="aspect-[4/3] bg-slate-100">
              {product.image ? (
                <img src={product.image} alt={product.name} className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full items-center justify-center text-[11px] uppercase tracking-[0.28em] text-slate-400">
                  No image
                </div>
              )}
            </div>

            {product.gallery.length > 1 && (
              <div className="grid gap-3 border-t border-slate-100 p-4 sm:grid-cols-3">
                {product.gallery.slice(0, 6).map((image, index) => (
                  <div key={`${image}-${index}`} className="aspect-[4/3] overflow-hidden rounded-[18px] bg-slate-100">
                    <img
                      src={image}
                      alt={`${product.name} detail ${index + 1}`}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="surface-card p-8 md:p-10">
            <div className="flex flex-wrap gap-2">
              {product.featured && <span className="meta-pill">Featured</span>}
              <span className="meta-pill">{product.inStock ? 'In stock' : 'Sold out'}</span>
              {product.sku && <span className="meta-pill">SKU {product.sku}</span>}
            </div>

            <h1 className="mt-6 font-display text-5xl leading-none tracking-tight text-slate-900 md:text-6xl">
              {product.name}
            </h1>

            {product.price && (
              <p className="mt-5 text-[11px] uppercase tracking-[0.28em] text-slate-500">{product.price}</p>
            )}

            {product.shortDescription && (
              <p className="mt-5 text-base leading-8 text-slate-600 md:text-lg">{product.shortDescription}</p>
            )}

            <div className="section-divider mt-8 pt-8">
              <div className="grid gap-4 sm:grid-cols-2">
                <ProductFact
                  label="Availability"
                  value={product.inStock ? 'Ready to ship' : 'Currently unavailable'}
                />
                <ProductFact
                  label="Catalog status"
                  value={product.featured ? 'Featured release' : 'Standard listing'}
                />
              </div>
            </div>

            <div className="mt-8 space-y-4">
              {canStartPaymentLinkCheckout ? (
                <>
                  <div className="rounded-[24px] border border-slate-100 bg-slate-50/70 px-5 py-4 text-sm leading-6 text-slate-600">
                    This item uses a Stripe Payment Link. We will send you directly to Stripe to
                    finish checkout there, and Stripe will handle the confirmation screen and
                    receipt.
                  </div>

                  <div className="flex flex-wrap gap-4">
                    <button
                      type="button"
                      onClick={() => checkoutMutation.mutate()}
                      disabled={checkoutMutation.isPending}
                      className="primary-button disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {checkoutMutation.isPending ? 'Redirecting...' : 'Continue to Stripe'}
                    </button>
                    <Link to="/store" className="secondary-button">
                      Back to store
                    </Link>
                  </div>
                </>
              ) : canStartHostedCheckout ? (
                <>
                  <form
                    onSubmit={(event) => {
                      event.preventDefault();
                      checkoutMutation.mutate();
                    }}
                    className="space-y-4"
                  >
                    <div className="grid gap-4 md:grid-cols-[140px_minmax(0,1fr)]">
                      <label className="flex flex-col gap-2 text-sm text-slate-600">
                        <span className="text-[10px] uppercase tracking-[0.28em] text-slate-500">
                          Quantity
                        </span>
                        <select
                          value={quantity}
                          onChange={(event) => setQuantity(Number(event.target.value) || 1)}
                          className="field-input min-h-[56px] rounded-full px-5"
                        >
                          {Array.from({ length: maxCheckoutQuantity }, (_, index) => index + 1).map((value) => (
                            <option key={value} value={value}>
                              {value}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="flex flex-col gap-2 text-sm text-slate-600">
                        <span className="text-[10px] uppercase tracking-[0.28em] text-slate-500">
                          Email
                        </span>
                        <input
                          type="email"
                          value={customerEmail}
                          onChange={(event) => setCustomerEmail(event.target.value)}
                          placeholder="Optional for Stripe prefill"
                          autoComplete="email"
                          className="field-input min-h-[56px] rounded-full px-5"
                        />
                      </label>
                    </div>

                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <p className="max-w-xl text-sm leading-6 text-slate-500">
                        Stripe hosts the payment form. Your email is optional, but it helps prefill
                        checkout and ties the pending order back to you faster on return.
                      </p>
                      <button
                        type="submit"
                        disabled={checkoutMutation.isPending}
                        className="primary-button disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {checkoutMutation.isPending ? 'Redirecting...' : 'Checkout securely'}
                      </button>
                    </div>
                  </form>

                  <div className="flex flex-wrap gap-4">
                    <Link to="/store" className="secondary-button">
                      Back to store
                    </Link>
                  </div>
                </>
              ) : (
                <div className="flex flex-wrap gap-4">
                  <Link to="/contact" className="primary-button">
                    {product.inStock ? 'Inquire about this product' : 'Join the waitlist'}
                  </Link>
                  <Link to="/store" className="secondary-button">
                    Back to store
                  </Link>
                </div>
              )}
            </div>

            {checkoutMutation.error ? (
              <div className="mt-5 rounded-[22px] border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-700">
                {checkoutMutation.error.message || 'Secure checkout is unavailable right now.'}
              </div>
            ) : null}
          </section>
        </div>

        <div className="mt-8 grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
          <section className="surface-card px-8 py-10 md:px-10 md:py-12">
            <p className="eyebrow">Details</p>
            <h2 className="mt-5 text-3xl font-medium leading-tight text-slate-900 md:text-4xl">
              Product overview
            </h2>
            <div className="mt-6 whitespace-pre-line text-sm leading-8 text-slate-600 md:text-base">
              {description}
            </div>
          </section>

          {(product.categories.length > 0 || product.featured || product.sku) && (
            <aside className="surface-card px-8 py-10 md:px-10 md:py-12">
              <p className="eyebrow">Catalog</p>

              {product.categories.length > 0 && (
                <>
                  <h2 className="mt-5 text-2xl font-medium leading-tight text-slate-900">Tags</h2>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {product.categories.map((category) => (
                      <span key={category} className="meta-pill">
                        {category}
                      </span>
                    ))}
                  </div>
                </>
              )}

              {product.sku && (
                <div className={product.categories.length > 0 ? 'section-divider mt-8 pt-8' : 'mt-8'}>
                  <p className="text-[10px] uppercase tracking-[0.28em] text-slate-500">Reference</p>
                  <p className="mt-4 text-lg text-slate-900">{product.sku}</p>
                </div>
              )}
            </aside>
          )}
        </div>
      </article>
    </main>
  );
}
